import React from 'react';
import { TextField } from './TextField';
import { TextareaField } from './TextareaField';
import { RichTextField } from './RichTextField';
import { ImageUrlField } from './ImageUrlField';
import { LinksField } from './LinksField';
import { LogoLinksField } from './LogoLinksField';
import { HashtagCategoriesField } from './HashtagCategoriesField';
import { CreatorEntriesField } from './CreatorEntriesField';
import { BrandEntriesField } from './BrandEntriesField';
import { SlidesField } from './SlidesField';
import { HighlightsField } from './HighlightsField';
import { SocialField } from './SocialField';
import { BulletsField } from './BulletsField';
import { FieldProps } from '@/types/reports';
import { ColorField } from './ColorField';
import { SelectField } from './SelectField';
import { TooltipListField } from './TooltipListField';
import { UrlField } from './UrlField';
import { NewsSubsectionsField } from './NewsSubsectionsField';
import { ThematicSubsectionsField } from './ThematicSubsectionsField';
import { CuratorshipItemsField } from './CuratorshipItemsField';
import { ReportDateField } from './ReportDateField';

export type { FieldConfig } from '@/types/reports';

export const FieldFactory: React.FC<FieldProps> = ({ field, value, onChange, error }) => {
  const commonProps = {
    label: field.label,
    required: field.required,
    value: value || (field.type === 'links' || field.type === 'logo-links' || field.type === 'hashtag-categories' || field.type === 'creator-entries' || field.type === 'brand-entries' || field.type === 'slides' || field.type === 'highlights' || field.type === 'social' || field.type === 'bullets' || field.type === 'tooltip-list' || field.type === 'news-subsections' || field.type === 'thematic-subsections' || field.type === 'curatorship-items' ? [] : ''),
    onChange,
    placeholder: field.placeholder || `Digite ${field.label.toLowerCase()}`,
    error
  };

  const isColorField =
    field.type === 'text' && field.key.toLowerCase().includes('color');

  switch (field.type) {
    case 'select':
      return (
        <SelectField
          label={field.label}
          required={field.required}
          value={value || ''}
          onChange={onChange}
          options={field.options || []}
          placeholder={field.placeholder}
          error={error}
        />
      );
    case 'date':
      return (
        <ReportDateField
          label={field.label}
          required={field.required}
          value={value || ''}
          onChange={onChange}
          placeholder={field.placeholder}
          error={error}
        />
      );

    case 'text':
      if (isColorField) {
        return <ColorField {...commonProps} />;
      }
      return <TextField {...commonProps} />;
    
    case 'textarea':
      return <TextareaField {...commonProps} rows={field.rows} />;
    
    case 'rich-text':
      return <RichTextField {...commonProps} />;
    
    case 'url':
      return <ImageUrlField {...commonProps} />;

    case 'url-text':
      return <UrlField {...commonProps} />;
    
    case 'links':
      return <LinksField {...commonProps} />;

    case 'logo-links':
      return (
        <LogoLinksField
          label={field.label}
          required={field.required}
          value={value || []}
          onChange={onChange}
          error={error}
        />
      );

    case 'hashtag-categories':
      return (
        <HashtagCategoriesField
          label={field.label}
          required={field.required}
          value={value || []}
          onChange={onChange}
          error={error}
        />
      );

    case 'creator-entries':
      return (
        <CreatorEntriesField
          label={field.label}
          required={field.required}
          value={value || []}
          onChange={onChange}
          error={error}
        />
      );

    case 'brand-entries':
      return (
        <BrandEntriesField
          label={field.label}
          required={field.required}
          value={value || []}
          onChange={onChange}
          error={error}
        />
      );
    
    case 'slides':
      return <SlidesField {...commonProps} />;

    case 'highlights':
      return (
        <HighlightsField
          label={field.label}
          required={field.required}
          value={value || []}
          onChange={onChange}
          error={error}
        />
      );

    case 'social':
      return (
        <SocialField
          label={field.label}
          required={field.required}
          value={value || []}
          onChange={onChange}
          error={error}
        />
      );

    case 'bullets':
      return (
        <BulletsField
          label={field.label}
          required={field.required}
          value={value || []}
          onChange={onChange}
          error={error}
        />
      );

    case 'tooltip-list':
      return (
        <TooltipListField
          label={field.label}
          required={field.required}
          value={value || []}
          onChange={onChange}
          error={error}
        />
      );

    case 'news-subsections':
      return (
        <NewsSubsectionsField
          label={field.label}
          required={field.required}
          value={value || []}
          onChange={onChange}
          error={error}
        />
      );

    case 'thematic-subsections':
      return (
        <ThematicSubsectionsField
          label={field.label}
          required={field.required}
          value={value || []}
          onChange={onChange}
          error={error}
        />
      );

    case 'curatorship-items':
      return (
        <CuratorshipItemsField
          label={field.label}
          required={field.required}
          value={value || []}
          onChange={onChange}
          error={error}
        />
      );
    
    case 'image':
      return <ImageUrlField {...commonProps} />;
    
    default:
      return <TextField {...commonProps} />;
  }
};
